/**
 * Serializable configuration interfaces for particle effects.
 * Covers thruster flame (shader), thruster sparks (shader), and atmosphere wisps (three.quarks).
 * Default values match the previously hardcoded constants in each effect's shader/source.
 */
// ---- Default values (match previously hardcoded constants) ----
export const DEFAULT_THRUSTER_FLAME = {
    // Geometry
    flameWidth: 0.8,
    flameLength: 4.0,
    // Taper
    nozzleWidth: 0.15,
    peakWidth: 0.50,
    // Length
    flameLenNormal: 0.70,
    flameLenAfterburner: 0.95,
    // Turbulent edges
    flowSpeed: 8.0,
    flowSpeedBoost: 4.0,
    distortionBase: 0.15,
    distortionTipAdd: 0.25,
    // Mach diamonds
    machFreqNormal: 8.0,
    machFreqAfterburner: 14.0,
    machBandMin: 0.7,
    machBandRange: 0.3,
    // Internal turbulence
    turbCoarseScale: 3.0,
    turbFineScale: 8.0,
    turbBase: 0.7,
    turbCoarseAmp: 0.2,
    turbFineAmp: 0.1,
    // Flicker
    flickerGlobalFreq: 37.7,
    flickerGlobalAmp: 0.1,
    flickerLocalFreq: 53.1,
    flickerLocalAmp: 0.05,
    // Colors
    normalCore: [1.0, 0.95, 0.85],
    normalMid: [1.0, 0.6, 0.15],
    normalTip: [0.8, 0.15, 0.02],
    abCore: [0.8, 0.9, 1.0],
    abMid: [0.3, 0.7, 1.0],
    abTip: [0.05, 0.15, 0.8],
    // HDR
    hdrMultNormal: 2.5,
    hdrMultAfterburner: 4.0,
    nozzleBoostMax: 2.0,
};
export const DEFAULT_THRUSTER_SPARKS = {
    sparkCount: 48,
    plumeSeedThreshold: 0.6,
    sparkLifetime: 0.4,
    plumeLifetime: 0.8,
    sparkSpeed: 8.0,
    plumeSpeed: 4.0,
    afterburnerSpeedBoost: 0.6,
    coneAngle: 0.7,
    nozzleHalfWidth: 0.06,
    sparkDrag: 3.0,
    plumeDrag: 2.0,
    sparkSize: 3.0,
    plumeSize: 6.0,
    sparkEdgeSoftness: 0.1,
    plumeEdgeSoftness: 0.4,
    normalHot: [1.0, 0.95, 0.85],
    normalMid: [1.0, 0.6, 0.15],
    normalCool: [0.8, 0.15, 0.02],
    abHot: [0.8, 0.9, 1.0],
    abMid: [0.3, 0.7, 1.0],
    abCool: [0.05, 0.15, 0.8],
    flickerFreq: 47.3,
    flickerAmp: 0.15,
    hdrMultNormal: 2.0,
    hdrMultAfterburner: 3.5,
};
export const DEFAULT_ATMOSPHERE_WISPS = {
    radiusMultiplier: 1.1,
    thickness: 0.1,
    emissionRate: 15,
    lifetimeMin: 3,
    lifetimeMax: 6,
    startSpeed: 0.1,
    sizeMin: 1.0,
    sizeMax: 4.0,
    startColor: [0.3, 0.5, 1.0, 0.15],
    colorStops: [
        [[0.3, 0.5, 1.2], 0],
        [[0.2, 0.4, 1.0], 0.5],
        [[0.1, 0.2, 0.6], 1.0],
    ],
    alphaKeys: [
        [0, 0],
        [0.15, 0.15],
        [0.15, 0.5],
        [0, 1.0],
    ],
    orbitSpeed: 0.2,
    sizeCurve: [0.5, 1, 1, 0.5],
    texture: '/assets/particles/smoke_01.png',
};
/** Default configs for all effect types. */
export const PARTICLE_EFFECT_DEFAULTS = {
    thruster_flame: DEFAULT_THRUSTER_FLAME,
    thruster_sparks: DEFAULT_THRUSTER_SPARKS,
    atmosphere_wisps: DEFAULT_ATMOSPHERE_WISPS,
};
/** Effect metadata for the editor. */
export const PARTICLE_EFFECT_ENTRIES = [
    { id: 'thruster_flame', name: 'Thruster Flame', type: 'shader_flame', config: DEFAULT_THRUSTER_FLAME },
    { id: 'thruster_sparks', name: 'Thruster Sparks', type: 'shader_sparks', config: DEFAULT_THRUSTER_SPARKS },
    { id: 'atmosphere_wisps', name: 'Atmosphere Wisps', type: 'quarks_atmosphere', config: DEFAULT_ATMOSPHERE_WISPS },
];
// ---- Validation ----
function isObject(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function isNumber(v) {
    return typeof v === 'number' && !Number.isNaN(v) && Number.isFinite(v);
}
function isRGB(v) {
    return Array.isArray(v) && v.length === 3 && v.every(isNumber);
}
function isRGBA(v) {
    return Array.isArray(v) && v.length === 4 && v.every(isNumber);
}
/**
 * Validate a ThrusterFlameConfig. Returns null on success, error string on failure.
 */
export function validateThrusterFlameConfig(data) {
    if (!isObject(data))
        return 'ThrusterFlameConfig must be an object';
    const numericKeys = [
        'flameWidth', 'flameLength', 'nozzleWidth', 'peakWidth',
        'flameLenNormal', 'flameLenAfterburner',
        'flowSpeed', 'flowSpeedBoost', 'distortionBase', 'distortionTipAdd',
        'machFreqNormal', 'machFreqAfterburner', 'machBandMin', 'machBandRange',
        'turbCoarseScale', 'turbFineScale', 'turbBase', 'turbCoarseAmp', 'turbFineAmp',
        'flickerGlobalFreq', 'flickerGlobalAmp', 'flickerLocalFreq', 'flickerLocalAmp',
        'hdrMultNormal', 'hdrMultAfterburner', 'nozzleBoostMax',
    ];
    for (const key of numericKeys) {
        if (!isNumber(data[key]))
            return `${key} must be a finite number`;
    }
    const colorKeys = [
        'normalCore', 'normalMid', 'normalTip', 'abCore', 'abMid', 'abTip',
    ];
    for (const key of colorKeys) {
        if (!isRGB(data[key]))
            return `${key} must be an [r,g,b] array`;
    }
    return null;
}
/**
 * Validate a ThrusterSparksConfig. Returns null on success, error string on failure.
 */
export function validateThrusterSparksConfig(data) {
    if (!isObject(data))
        return 'ThrusterSparksConfig must be an object';
    const numericKeys = [
        'sparkCount', 'plumeSeedThreshold',
        'sparkLifetime', 'plumeLifetime',
        'sparkSpeed', 'plumeSpeed', 'afterburnerSpeedBoost',
        'coneAngle', 'nozzleHalfWidth',
        'sparkDrag', 'plumeDrag',
        'sparkSize', 'plumeSize',
        'sparkEdgeSoftness', 'plumeEdgeSoftness',
        'flickerFreq', 'flickerAmp',
        'hdrMultNormal', 'hdrMultAfterburner',
    ];
    for (const key of numericKeys) {
        if (!isNumber(data[key]))
            return `${key} must be a finite number`;
    }
    if (!Number.isInteger(data.sparkCount) || data.sparkCount < 1) {
        return 'sparkCount must be a positive integer';
    }
    const colorKeys = [
        'normalHot', 'normalMid', 'normalCool', 'abHot', 'abMid', 'abCool',
    ];
    for (const key of colorKeys) {
        if (!isRGB(data[key]))
            return `${key} must be an [r,g,b] array`;
    }
    return null;
}
/**
 * Validate an AtmosphereWispsConfig. Returns null on success, error string on failure.
 */
export function validateAtmosphereWispsConfig(data) {
    if (!isObject(data))
        return 'AtmosphereWispsConfig must be an object';
    const numericKeys = [
        'radiusMultiplier', 'thickness', 'emissionRate',
        'lifetimeMin', 'lifetimeMax', 'startSpeed', 'sizeMin', 'sizeMax',
        'orbitSpeed',
    ];
    for (const key of numericKeys) {
        if (!isNumber(data[key]))
            return `${key} must be a finite number`;
    }
    if (!isRGBA(data.startColor))
        return 'startColor must be an [r,g,b,a] array';
    if (!Array.isArray(data.colorStops))
        return 'colorStops must be an array';
    for (let i = 0; i < data.colorStops.length; i++) {
        const stop = data.colorStops[i];
        if (!Array.isArray(stop) || stop.length !== 2)
            return `colorStops[${i}] must be [color, position]`;
        if (!isRGB(stop[0]))
            return `colorStops[${i}][0] must be [r,g,b]`;
        if (!isNumber(stop[1]))
            return `colorStops[${i}][1] must be a number`;
    }
    if (!Array.isArray(data.alphaKeys))
        return 'alphaKeys must be an array';
    for (let i = 0; i < data.alphaKeys.length; i++) {
        const key = data.alphaKeys[i];
        if (!Array.isArray(key) || key.length !== 2)
            return `alphaKeys[${i}] must be [alpha, position]`;
        if (!isNumber(key[0]) || !isNumber(key[1]))
            return `alphaKeys[${i}] values must be numbers`;
    }
    if (!Array.isArray(data.sizeCurve) || data.sizeCurve.length !== 4 || !data.sizeCurve.every(isNumber)) {
        return 'sizeCurve must be [p1, p2, p3, p4]';
    }
    if (typeof data.texture !== 'string')
        return 'texture must be a string';
    return null;
}
/** Map of effect IDs to their validation functions. */
const VALIDATORS = {
    thruster_flame: validateThrusterFlameConfig,
    thruster_sparks: validateThrusterSparksConfig,
    atmosphere_wisps: validateAtmosphereWispsConfig,
};
/** Valid effect IDs. */
export const VALID_EFFECT_IDS = ['thruster_flame', 'thruster_sparks', 'atmosphere_wisps'];
/**
 * Validate a particle effect config for a given effect ID.
 * Returns null on success, error string on failure.
 */
export function validateParticleEffectConfig(effectId, config) {
    if (!VALID_EFFECT_IDS.includes(effectId)) {
        return `Unknown effect ID: ${effectId}`;
    }
    return VALIDATORS[effectId](config);
}
//# sourceMappingURL=particleEffectTypes.js.map